import { RefObject, useLayoutEffect } from 'react';

// Puts a full-screen MUI dialog in the document flow so the window scrolls it
export const pageScrollDialogSx = {
  position: 'relative',
  '& .MuiDialog-container': { height: 'auto' },
  '& .MuiDialog-paper': {
    height: 'auto',
    minHeight: '100vh',
    overflow: 'visible',
  },
} as const;

// In page-scroll mode the scroll area doesn't scroll itself, the window does
export function scrollOverlayToTop(
  scrollArea: HTMLElement,
  behavior: ScrollBehavior = 'auto'
) {
  const isWindowScroll = getComputedStyle(scrollArea).overflowY === 'visible';
  (isWindowScroll ? window : scrollArea).scrollTo({ top: 0, behavior });
}

// While enabled, the page behind the overlay is hidden and the window scrolls
// the overlay, so iOS Safari can shrink its toolbar. Pair with pageScrollDialogSx.
export function usePageScrollOverlay(
  enabled: boolean,
  scrollRef: RefObject<HTMLElement | null>
) {
  useLayoutEffect(() => {
    if (!enabled) return;
    const pageRoot = document.getElementById('__next');
    const scrollArea = scrollRef.current;
    const pageScrollY = window.scrollY;
    let overlayScrollY = scrollArea?.scrollTop ?? 0;
    if (pageRoot) pageRoot.style.display = 'none';
    window.scrollTo({ top: overlayScrollY, behavior: 'instant' });
    const handleScroll = () => {
      overlayScrollY = window.scrollY;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      // Runs after the fixed layout is back: restore the page, keep the overlay where it was
      window.removeEventListener('scroll', handleScroll);
      if (pageRoot) pageRoot.style.display = '';
      window.scrollTo({ top: pageScrollY, behavior: 'instant' });
      scrollArea?.scrollTo({ top: overlayScrollY, behavior: 'instant' });
    };
  }, [enabled, scrollRef]);
}
